import { Box, Card, CardActionArea, Chip, Stack, Typography } from '@mui/material';
import PlaceIcon from '@mui/icons-material/PlaceOutlined';
import BoltIcon from '@mui/icons-material/BoltOutlined';
import EventIcon from '@mui/icons-material/EventOutlined';
import PlayIcon from '@mui/icons-material/PlayCircleOutline';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/lib/format';
import type { Project } from '@/types/api';

/** A project teaser. The whole card is one link, so the target is easy to hit. */
export function ProjectCard({ project }: { project: Project }) {
  const { i18n } = useTranslation();
  const isDe = i18n.resolvedLanguage !== 'en';
  const lang = i18n.resolvedLanguage;

  const facts = [
    project.location && { Icon: PlaceIcon, text: project.location },
    project.sizeKwp && { Icon: BoltIcon, text: `${project.sizeKwp} kWp` },
    project.completedAt && { Icon: EventIcon, text: formatDate(project.completedAt, lang) },
  ].filter(Boolean) as Array<{ Icon: typeof PlaceIcon; text: string }>;

  return (
    <Card sx={{ height: '100%', overflow: 'hidden' }}>
      <CardActionArea
        component={RouterLink}
        to={`/projekte/${project.slug}`}
        sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
      >
        <Box sx={{ position: 'relative' }}>
          {project.imageUrl ? (
            <Box
              component="img"
              src={project.imageUrl}
              alt={isDe ? project.titleDe : project.titleEn}
              loading="lazy"
              sx={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <Box sx={{ width: '100%', aspectRatio: '4 / 3', bgcolor: 'action.hover' }} />
          )}
          {project.videoUrl && (
            <PlayIcon
              sx={{ position: 'absolute', right: 12, bottom: 12, fontSize: 34, color: 'common.white' }}
            />
          )}
        </Box>

        <Stack spacing={1.5} sx={{ p: { xs: 2.5, md: 3 }, flex: 1 }}>
          <Typography variant="h4" component="h3">
            {isDe ? project.titleDe : project.titleEn}
          </Typography>

          {facts.length > 0 && (
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              {facts.map(({ Icon, text }) => (
                <Stack key={text} direction="row" spacing={0.5} alignItems="center">
                  <Icon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {text}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}

          <Typography variant="body2" color="text.secondary">
            {isDe ? project.descriptionDe : project.descriptionEn}
          </Typography>

          {(isDe ? project.componentsDe : project.componentsEn) && (
            <Chip
              size="small"
              variant="outlined"
              label={isDe ? project.componentsDe : project.componentsEn}
              sx={{ alignSelf: 'flex-start', maxWidth: '100%', mt: 'auto' }}
            />
          )}
        </Stack>
      </CardActionArea>
    </Card>
  );
}
